import React, { useEffect, useState } from 'react';
import DataTableHeader from '../../../components/DataTableHeader';
import { translate } from '../../../services/translate';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import CustomButton from '../../../components/CustomButton';
import DataTableContainer from '../../../components/DataTableContainer';
import MyDataTable from '../../../components/MyDataTable';
import Badge from '../../../components/Badge';
import DropdownButton from '../../../components/DropdownButton';
import moment from 'moment';
import ProtectiveMeasureModal from './ProtectiveMeasureModal';
import AlertModal from '../../../components/AlertModal';
import { TranslateRich } from '../../../components/TranslateRich';
import { ClientError, InvalidValue } from '../../../services/central-api/base-service';
import { guardianAppService } from '../../../services/central-api/guardian-app';
import Toast from 'react-native-toast-message';

interface ProtectiveMeasureSaveProgress {
    measure: ProtectiveMeasureDraft;
    uploadedPhoto?: File;
    removedPhotoId?: number;
    policeReportSaved?: boolean;
}

export default function ProtectiveMeasuresTab({ guardianWomanId, setProtectiveMeasures, protectiveMeasures }: {
    guardianWomanId?: number;
    setProtectiveMeasures: React.Dispatch<React.SetStateAction<ProtectiveMeasureDraft[]>>;
    protectiveMeasures: ProtectiveMeasureDraft[];
}) {
    const [textFilter, setTextFilter] = useState('');
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [isDestroyModalVisible, setDestroyModalVisible] = useState<boolean>(false);
    const [isRevokeModalVisible, setRevokeModalVisible] = useState<boolean>(false);
    const [selectedProtectiveMeasure, setSelectedProtectiveMeasure] = useState<ProtectiveMeasureDraft>();
    const [filteredProtectiveMeasures, setFilteredProtectiveMeasures] = useState<ProtectiveMeasureDraft[]>([]);

    useEffect(() => {
        const filteredProtectiveMeasures = protectiveMeasures.filter(pm => pm.name.toUpperCase().includes(textFilter.toUpperCase()));
        setFilteredProtectiveMeasures(filteredProtectiveMeasures);
    }, [protectiveMeasures, textFilter]);

    function getBlankProtectiveMeasure(): ProtectiveMeasureDraft {
        return {
            id: -Date.now() - Math.random(),
            aggressorElectronicMonitoring: null,
            aggressorImages: [],
            aggressorPhotoFiles: [],
            aggressorPhotoIdsToDelete: [],
            aggressorNotified: null,
            aggressorProvisionalRelease: null,
            attemptedFemicide: null,
            blobStorageId: null,
            caseNumber: '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            deletedAt: null,
            document: '',
            firearmUsed: null,
            guardianWomanId: -1,
            hospitalizationDueToIncident: null,
            integrationId: null,
            name: '',
            observation: '',
            occurrenceDate: null,
            policeReportUrl: '',
            prosecutorOffice: '',
            severeInjury: null,
            victimPregnant: null,
            removePoliceReport: false,
        };
    }

    function publishMeasure(previousId: number | undefined, measure: ProtectiveMeasureDraft) {
        setProtectiveMeasures(current => {
            const index = current.findIndex(existing => existing.id === previousId);
            if (index < 0) {
                return [...current, measure];
            }
            const copy = [...current];
            copy[index] = measure;
            return copy;
        });
        setSelectedProtectiveMeasure(measure);
    }

    async function onSave(protectiveMeasure: ProtectiveMeasureDraft, onProgress: (progress: ProtectiveMeasureSaveProgress) => void) {
        if (!guardianWomanId) {
            const pendingMeasure = {
                ...protectiveMeasure,
                id: protectiveMeasure.id ?? getBlankProtectiveMeasure().id,
                guardianWomanId: -1,
            };
            publishMeasure(protectiveMeasure.id, pendingMeasure);
            return;
        }

        const previousId = protectiveMeasure.id;
        let savedMeasure: ProtectiveMeasureDraft;
        if (!previousId || previousId < 0) {
            const created = await guardianAppService.createProtectiveMeasure(guardianWomanId, protectiveMeasure);
            savedMeasure = {
                ...protectiveMeasure,
                ...created,
                id: created.id,
                guardianWomanId,
                aggressorImages: created.aggressorImages ?? protectiveMeasure.aggressorImages,
                removePoliceReport: false,
            };
            publishMeasure(previousId, savedMeasure);
            onProgress({ measure: savedMeasure, policeReportSaved: true });
        } else {
            const existingMeasure = protectiveMeasures.find(measure => measure.id === previousId);
            const updated = await guardianAppService.editProtectiveMeasure(
                guardianWomanId,
                previousId,
                protectiveMeasure,
            );
            const aggressorImages = updated.aggressorImages?.map(photo => {
                const existingPhoto = protectiveMeasure.aggressorImages.find(existing => existing.id === photo.id);
                return {
                    ...existingPhoto,
                    ...photo,
                    file: existingPhoto?.file ?? photo.file,
                    fileName: existingPhoto?.fileName ?? photo.fileName,
                };
            });
            savedMeasure = {
                ...protectiveMeasure,
                ...updated,
                id: previousId,
                guardianWomanId,
                aggressorImages: aggressorImages ?? protectiveMeasure.aggressorImages,
                policeReportFile: protectiveMeasure.removePoliceReport
                    ? undefined
                    : protectiveMeasure.policeReportFile ?? existingMeasure?.policeReportFile,
                removePoliceReport: false,
            };
            publishMeasure(previousId, savedMeasure);
            onProgress({ measure: savedMeasure, policeReportSaved: true });
        }

        if (previousId && previousId > 0) {
            for (const photoId of [...savedMeasure.aggressorPhotoIdsToDelete]) {
                await guardianAppService.deleteProtectiveMeasurePhoto(guardianWomanId, savedMeasure.id!, photoId);
                savedMeasure = {
                    ...savedMeasure,
                    aggressorPhotoIdsToDelete: savedMeasure.aggressorPhotoIdsToDelete.filter(id => id !== photoId),
                    aggressorImages: savedMeasure.aggressorImages.filter(photo => photo.id !== photoId),
                };
                publishMeasure(savedMeasure.id, savedMeasure);
                onProgress({ measure: savedMeasure, removedPhotoId: photoId });
            }
        }

        while (savedMeasure.aggressorPhotoFiles.length > 0) {
            const photoFile = savedMeasure.aggressorPhotoFiles[0];
            const createdPhoto = await guardianAppService.addProtectiveMeasurePhoto(guardianWomanId, savedMeasure.id!, photoFile);
            savedMeasure = {
                ...savedMeasure,
                aggressorImages: [...savedMeasure.aggressorImages, { ...createdPhoto, file: photoFile, fileName: photoFile.name }],
                aggressorPhotoFiles: savedMeasure.aggressorPhotoFiles.slice(1),
            };
            publishMeasure(savedMeasure.id, savedMeasure);
            onProgress({ measure: savedMeasure, uploadedPhoto: photoFile });
        }

        Toast.show({ type: 'sentinelxSuccess', text1: translate('ActionSuccessfully') });
    }

    useEffect(() => {
        if (!isModalVisible) {
            setSelectedProtectiveMeasure(getBlankProtectiveMeasure());
        }
    }, [isModalVisible]);

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            const filteredProtectiveMeasures = protectiveMeasures.filter(pm => pm.name.toUpperCase().includes(textFilter.toUpperCase()));
            setFilteredProtectiveMeasures(filteredProtectiveMeasures);
        }
    }

    return <>
        <DataTableHeader
            title={translate('registeredProtectiveMeasures')}
            zIndex={2}
            filterInput={<FilterInput
                value={textFilter ?? ''}
                onChange={text => {
                    setTextFilter(text);
                }}
                placeholder={translate('typeToSearch')}
                onKeyPress={handleKeyDown}
            />}
            actions={<CustomButton
                text={translate('register')}
                onPress={() => {
                    setModalVisible(true);
                    setSelectedProtectiveMeasure(getBlankProtectiveMeasure());
                }}
                type='secondary'
            />}
        />
        <DataTableContainer
            dataTable={<MyDataTable
                columns={[{
                    name: translate('status'),
                    cell: row => <View style={{ minWidth: 120, justifyContent: 'center', alignItems: 'flex-start' }}>
                        <Badge
                            mode='SQUARE'
                            color={row.deletedAt === null ? (row.id! > 0 ? 'green' : 'orange') : 'red'}
                        >{translate(row.deletedAt === null ? (row.id! > 0 ? 'active_f' : 'pending') : 'revoked_f')}</Badge>
                    </View>,
                    maxWidth: '130px',
                    minWidth: '130px',
                },
                {
                    name: translate('aggressorName'),
                    selector: row => row.name ?? '',
                    wrap: true,
                    maxWidth: '400px',
                },
                {
                    name: translate('createdAt'),
                    selector: row => moment(row.createdAt).format('DD/MM/YYYY'),
                    maxWidth: '130px',
                    minWidth: '130px',
                },
                {
                    name: '',
                    cell: row => <View style={{ minWidth: 120, justifyContent: 'center', alignItems: 'flex-start' }}>
                        {row.integrationId && <Badge
                            mode='SQUARE'
                            color={'teal'}
                        >{translate('integration')}</Badge>}
                    </View>,
                },
                {
                    button: true,
                    cell: row =>
                        <DropdownButton
                            size={200}
                            actions={[{
                                content: translate(row.integrationId || row.deletedAt ? 'checkInformation' : 'editInformation'),
                                onPress: () => {
                                    setModalVisible(true);
                                    setSelectedProtectiveMeasure(row);
                                },
                            }, {
                                content: translate('revokeProtectiveMeasure'),
                                variant: 'danger',
                                onPress: () => {
                                    setSelectedProtectiveMeasure(row);
                                    setRevokeModalVisible(true);
                                },
                                omit: Boolean(row.integrationId) || row.id! < 0 || Boolean(row.deletedAt),
                            }, {
                                content: translate('delete'),
                                variant: 'danger',
                                onPress: () => {
                                    setSelectedProtectiveMeasure(row);
                                    setDestroyModalVisible(true);
                                },
                                omit: Boolean(row.integrationId) || row.id! > 0,
                            }]}
                        />
                }]}
                data={filteredProtectiveMeasures || []}
            />}
        />
        <ProtectiveMeasureModal
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            protectiveMeasure={selectedProtectiveMeasure}
            onSave={onSave}
        />
        <AlertModal
            type='destructive'
            isModalVisible={isDestroyModalVisible}
            setModalVisible={setDestroyModalVisible}
            title={translate('delete')}
            description={<>
                <TranslateRich i18nKey={'deleteProtectiveMeasureDescription'} />
                <br />
                <br />
                <TranslateRich i18nKey={'proceedWithDeletion'} />
            </>}
            onSubmit={async () => {
                const id = selectedProtectiveMeasure?.id;
                setProtectiveMeasures(current => current.filter(pm => pm.id !== id));
            }}
        />
        <AlertModal
            type='destructive'
            isModalVisible={isRevokeModalVisible}
            setModalVisible={setRevokeModalVisible}
            title={translate('revokeProtectiveMeasure')}
            customButtonLabel={translate('revoke')}
            description={<>
                <TranslateRich i18nKey={'revokeProtectiveMeasureDescription'} />
                <br />
                <br />
                <TranslateRich i18nKey={'proceedWithRevocation'} />
            </>}
            onSubmit={async () => {
                try {
                    const measure = selectedProtectiveMeasure;
                    if (!measure) {
                        return;
                    }
                    if (guardianWomanId && measure.id) {
                        await guardianAppService.deleteProtectiveMeasure(guardianWomanId, measure.id);
                        Toast.show({ type: 'sentinelxSuccess', text1: translate('ActionSuccessfully') });
                    }
                    publishMeasure(measure.id, { ...measure, deletedAt: new Date().toISOString() });
                } catch (error) {
                    showSaveError(error);
                    return false;
                }
            }}
        />
    </>;
}

function showSaveError(error: unknown) {
    if (error instanceof InvalidValue) {
        Toast.show({ type: 'sentinelxWarning', text1: translate('invalidValue', { field: error.field }) });
    } else if (error instanceof ClientError) {
        Toast.show({ type: 'sentinelxWarning', text1: translate(error.message) });
    } else {
        console.error(error);
        Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
    }
}
